/**
 * useEventDiscussion — the press logic behind every «open this event's
 * discussion» affordance on an event card (the icon-only EventChatIcon and the
 * labelled EventDiscussButton, CORE-1696). One place resolves the discussion
 * and reports the «unavailable» outcome, so both controls behave the same.
 */
import { useState } from 'react';
import { Alert, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { openEventDiscussion } from '../../utils/openEventDiscussion';
import { DELETED_DISCUSSION_NOTICE } from '../dashboard/discussionManagement';

/**
 * CORE-1264 — shown when the master behind the event can't be resolved, so a
 * tap is never a silent no-op. Calm, no exclamation (brand voice).
 */
export const DISCUSSION_UNAVAILABLE_MESSAGE = 'Обсуждение этого события пока недоступно';

export interface EventDiscussionRef {
  /** Auth user id of the event's master. Provide this or masterProfileId. */
  masterUserId?: string | null;
  /** Event entity id (Live.id / WatchParty.id / MasterEvent.id). */
  refId: number;
  /** Expected discussion `linkedEntityType`; null means «no discussion». */
  linkedEntityType: string | null;
  /** Master profile id. Provide this or masterUserId. */
  masterProfileId?: number | null;
  /** CORE-1654 — discussion id when the card already carries it (calendar). */
  discussionId?: string | null;
  /** CORE-1654 — the card is the event's feed announcement; see openEventDiscussion. */
  announced?: boolean;
  masterName?: string | null;
}

export function useEventDiscussion({
  masterUserId, refId, linkedEntityType, masterProfileId, discussionId, announced, masterName,
}: EventDiscussionRef) {
  const navigation = useNavigation<any>();
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // No discussion is possible without a master ref or for non-event kinds.
  // Calendar mappers put 0 where the profile id is unknown — that's not a ref.
  const hasProfileId = masterProfileId != null && masterProfileId > 0;
  const available = (!!masterUserId || hasProfileId) && !!linkedEntityType;

  const onPress = async (e?: { stopPropagation?: () => void }) => {
    // Event cards are themselves pressable — don't trigger the card's onPress.
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    const result = await openEventDiscussion({
      masterUserId,
      refId,
      linkedEntityType,
      masterProfileId,
      discussionId,
      announced,
      masterName,
      navigation,
    }).finally(() => setBusy(false));
    if (result === 'unresolved') {
      // BrandToast is position:fixed on web; inside a card on native it would
      // be clipped, so the system alert carries the message there.
      if (Platform.OS === 'web') setUnavailable(true);
      else Alert.alert(DISCUSSION_UNAVAILABLE_MESSAGE);
    } else if (result === 'deleted' && Platform.OS !== 'web') {
      // CORE-1654 — web has opened the chat tab in the deleted state; native
      // has no such screen, so the notice is the whole answer.
      Alert.alert(DELETED_DISCUSSION_NOTICE);
    }
  };

  return { available, busy, unavailable, dismissUnavailable: () => setUnavailable(false), onPress };
}
